import React, { useEffect, useState } from 'react';
import Modal, { MODAL_ERROR } from '../Modal';
import Button from '../Button';
import { plannerService } from '../../services';

const money = (cents) => `$${(cents / 100).toFixed(0)}`;

/** What each of them gets, in their words rather than ours. */
const HOW = {
  offer: 'A booking offer in their Pro desk',
  request: 'An email or text — this page hasn’t been claimed',
};

/**
 * BOOK IT, asked before it is done.
 *
 * The button used to fan real booking offers out to claimed Pro pages and real emails and texts
 * to unclaimed ones on a single tap, with no way to see who that was until afterwards — and
 * afterwards was a notification saying "0 offers and requests went out" when the plan had
 * nobody in it to ask.
 *
 * The list comes from the server (`GET /plans/{id}/book/preview`), assembled by the same walk
 * that does the contacting, so what is agreed to here is what gets contacted. Opening this
 * sends nothing.
 */
export default function PlannerBookModal({ isOpen, planId, onClose, onBooked }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !planId) return;
    let gone = false;
    setLoading(true); setError(''); setPreview(null);
    plannerService.bookPreview(planId).then((r) => {
      if (gone) return;
      if (r.success) setPreview(r.data);
      else setError(r.error || 'Could not read the plan.');
      setLoading(false);
    });
    // eslint-disable-next-line consistent-return
    return () => { gone = true; };
  }, [isOpen, planId]);

  const send = async () => {
    setSending(true); setError('');
    const r = await plannerService.book(planId);
    setSending(false);
    if (!r.success) { setError(r.error || 'Could not send them.'); return; }
    onBooked?.(r.data);
    onClose();
  };

  const toAsk = (preview?.picks || []).filter((p) => !p.alreadyAsked);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="REACH OUT TO THESE PEOPLE?">
      <div className="space-y-3 font-mono">
        {loading && <p className="text-xs text-gray-500">READING THE PLAN…</p>}

        {!loading && preview?.empty && (
          <>
            <p className="text-sm">There's nobody to ask on this plan.</p>
            <p className="text-[11px] text-gray-500">
              The venue is your own, and no sound or staff has been chosen — so there are no
              businesses to contact. Pick some on the plan first, or run the event yourself.
            </p>
            <Button fullWidth variant="secondary" onClick={onClose}>CLOSE</Button>
          </>
        )}

        {!loading && preview && !preview.empty && (
          <>
            <p className="text-[11px] text-gray-500">
              Sending this asks each of them, for real, now. Nothing is charged — they answer
              first, and their answers land in your Pro desk.
            </p>

            <ul className="overflow-hidden rounded-2xl bg-[#F5F5F5] divide-y-2 divide-white">
              {preview.picks.map((p) => (
                <li key={p.businessId} className={`p-3 ${p.alreadyAsked ? 'opacity-45' : ''}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{p.role}</span>
                    <span className="text-xs font-bold">{money(p.amountCents)}</span>
                  </div>
                  <p className="text-sm font-bold">{p.name}</p>
                  <p className="text-[11px] text-gray-500">
                    {p.alreadyAsked ? 'Already asked — this one is skipped' : HOW[p.isListed ? 'request' : 'offer']}
                  </p>
                </li>
              ))}
            </ul>

            <div className="flex items-baseline justify-between text-xs">
              <span className="font-bold uppercase tracking-wider">{toAsk.length} TO ASK</span>
              <span className="font-bold">{money(preview.totalCents)}</span>
            </div>

            {error && <div className={MODAL_ERROR} role="alert">{error}</div>}

            <div className="flex gap-2">
              <Button className="flex-1" variant="secondary" onClick={onClose} disabled={sending}>NOT YET</Button>
              <Button className="flex-1" onClick={send} disabled={sending || toAsk.length === 0}>
                {sending ? 'ASKING…' : `ASK ${toAsk.length === 1 ? 'THEM' : 'ALL ' + toAsk.length}`}
              </Button>
            </div>
          </>
        )}

        {!loading && !preview && error && (
          <>
            <div className={MODAL_ERROR} role="alert">{error}</div>
            <Button fullWidth variant="secondary" onClick={onClose}>CLOSE</Button>
          </>
        )}
      </div>
    </Modal>
  );
}
